import { ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type RectLike = Pick<
  DOMRectReadOnly,
  "top" | "right" | "bottom" | "left" | "width" | "height"
>;

const createRect = (
  left: number,
  top: number,
  right: number,
  bottom: number
): RectLike => ({
  left,
  top,
  right,
  bottom,
  width: Math.max(0, right - left),
  height: Math.max(0, bottom - top),
});

const horizontalOverlap = (a: RectLike, b: RectLike) =>
  Math.min(a.right, b.right) - Math.max(a.left, b.left);

const verticalOverlap = (a: RectLike, b: RectLike) =>
  Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);

const horizontalGap = (a: RectLike, b: RectLike) =>
  Math.max(0, Math.max(a.left, b.left) - Math.min(a.right, b.right));

const verticalGap = (a: RectLike, b: RectLike) =>
  Math.max(0, Math.max(a.top, b.top) - Math.min(a.bottom, b.bottom));

const mergePair = (a: RectLike, b: RectLike) =>
  createRect(
    Math.min(a.left, b.left),
    Math.min(a.top, b.top),
    Math.max(a.right, b.right),
    Math.max(a.bottom, b.bottom)
  );

const canMergeRects = (a: RectLike, b: RectLike, tolerance: number) => {
  if (rectsIntersect(a, b)) {
    return true;
  }

  const hGap = horizontalGap(a, b);
  const vGap = verticalGap(a, b);
  const hOverlap = horizontalOverlap(a, b);
  const vOverlap = verticalOverlap(a, b);

  const sharesVerticalBand = vOverlap > 0 || vGap <= tolerance;
  const sharesHorizontalBand = hOverlap > 0 || hGap <= tolerance;

  return (
    (hGap <= tolerance && sharesVerticalBand) ||
    (vGap <= tolerance && sharesHorizontalBand)
  );
};

export const rectsIntersect = (a: RectLike, b: RectLike) =>
  horizontalOverlap(a, b) > 0 && verticalOverlap(a, b) > 0;

export const getIntersectionRect = (a: RectLike, b: RectLike) => {
  if (!rectsIntersect(a, b)) {
    return null;
  }

  return createRect(
    Math.max(a.left, b.left),
    Math.max(a.top, b.top),
    Math.min(a.right, b.right),
    Math.min(a.bottom, b.bottom)
  );
};

export const translateRect = (rect: RectLike, originRect: RectLike) =>
  createRect(
    rect.left - originRect.left,
    rect.top - originRect.top,
    rect.right - originRect.left,
    rect.bottom - originRect.top
  );

export const mergeRects = (rects: RectLike[], tolerance = 0) => {
  const mergedRects: RectLike[] = [];
  const pending = [...rects]
    .filter((rect) => rect.width > 0 && rect.height > 0)
    .sort((a, b) => a.top - b.top || a.left - b.left);

  while (pending.length > 0) {
    let current = pending.shift() as RectLike;
    let hasMerged = true;

    while (hasMerged) {
      hasMerged = false;

      for (let index = 0; index < pending.length; index += 1) {
        const nextRect = pending[index];

        if (!canMergeRects(current, nextRect, tolerance)) {
          continue;
        }

        current = mergePair(current, nextRect);
        pending.splice(index, 1);
        hasMerged = true;
        break;
      }
    }

    mergedRects.push(current);
  }

  return mergedRects.sort((a, b) => a.top - b.top || a.left - b.left);
};
